import {
  Component,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  computed,
  inject,
  input,
  output,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';

import { map, Observable } from 'rxjs';

import { ButtonMode, ButtonTheme, ModalButtons } from '@fcom/ui-components';
import { CmsArticle } from '@fcom/core-api/interfaces';
import { FinnairThemeService } from '@fcom/common';
import { FragmentService } from '@fcom/common/services';
import { GtmService } from '@fcom/common/gtm';
import { ElementTypes, GaContext } from '@fcom/common/interfaces';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

const GTM_ELEMENT_NAME = 'kids-in-business-modal';

@Component({
  selector: 'fin-kids-in-business-notification',
  templateUrl: './kids-in-business-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class KidsInBusinessNotificationComponent implements OnInit {
  private fragmentService = inject(FragmentService);
  private gtmService = inject(GtmService);
  private cdr = inject(ChangeDetectorRef);
  private themeService = inject(FinnairThemeService);

  public readonly ModalButtons = ModalButtons;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonMode = ButtonMode;

  private readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);
  readonly continueButtonTheme = computed(() =>
    this.isNewBrand() ? ButtonTheme.PRIMARY_ACCENT : ButtonTheme.LIGHT_SECONDARY_OUTLINE
  );
  readonly cancelButtonTheme = computed(() =>
    this.isNewBrand() ? ButtonTheme.SECONDARY_OUTLINE : ButtonTheme.LIGHT_PRIMARY_CTA_HEATHER
  );
  public readonly headerStyles = ['font-heading-4 font-title-medium'];
  public content$: Observable<CmsArticle[]> | undefined;

  cancelButtonLabel = input.required<string>();
  continueButtonLabel = input.required<string>();
  contentFragmentKey = input.required<string>();

  modalOpen = false;
  checkboxForm = new FormGroup({ checked: new FormControl<boolean>(false, { nonNullable: true }) });
  showErrorMessage = false;

  continueButtonClick = output<void>();
  cancelButtonClick = output<void>();

  @ViewChild('errorMessage', { static: false })
  errorMessage: ElementRef | undefined;

  private destroyRef = inject(DestroyRef);

  constructor() {
    this.checkboxForm.controls.checked.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((checked) => {
      if (checked) {
        this.showErrorMessage = false;
      }
    });
  }

  ngOnInit(): void {
    this.content$ = this.fragmentService
      .getFragment<CmsArticle>(this.contentFragmentKey())
      .pipe(map((fragment) => fragment.items as CmsArticle[]));
  }

  openModal(): void {
    this.modalOpen = true;
    this.cdr.markForCheck();
    this.trackModalAction('open');
  }

  emitContinue(): void {
    if (this.checkboxForm.controls.checked.value) {
      this.checkboxForm.controls.checked.setValue(false);
      this.modalOpen = false;
      this.continueButtonClick.emit();
      this.trackModalAction('continue');
    } else {
      this.showErrorMessage = true;
      setTimeout(() => {
        this.errorMessage?.nativeElement.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }

  emitCancel(): void {
    this.checkboxForm.controls.checked.setValue(false);
    this.cancelButtonClick.emit();
    this.trackModalAction('cancel');
  }

  closeModal(): void {
    this.checkboxForm.controls.checked.setValue(false);
    this.modalOpen = false;
    this.trackModalAction('close');
  }

  private trackModalAction(action: string): void {
    this.gtmService.trackElement(GTM_ELEMENT_NAME, GaContext.BOOKING_FLOW, ElementTypes.MODAL, undefined, action);
  }
}
